import {
  AxiosInstance,
  AxiosInterceptorManager,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig
} from 'axios'

interface HttpClientConfig {
  timeout?: number
  [key: string]: unknown
}

interface AxiosClientWrapper {
  all<T>(values: Array<Promise<T> | T>): Promise<T[]>
  request<T = any, R = AxiosResponse<T>>(config: AxiosRequestConfig): Promise<R>
  retry: AxiosInstance
  interceptors: {
    request: AxiosInterceptorManager<InternalAxiosRequestConfig> // <--- Fix here
    response: AxiosInterceptorManager<AxiosResponse>
  }
}

interface HttpClientParams {
  config: HttpClientConfig
  axiosClient: AxiosClientWrapper
}

interface CallbackHandlers<T, R = void> {
  success: (value: AxiosResponse<T>) => R
  failure: (error: unknown) => R
}

class HttpClient {
  private config: HttpClientConfig
  private axiosClient: AxiosClientWrapper

  constructor({ config, axiosClient }: HttpClientParams) {
    this.config = config
    this.axiosClient = axiosClient
  }

  public getConfig = (): HttpClientConfig => {
    return this.config
  }

  public all = <T, R = void>(
    requests: Array<Promise<T> | T>,
    handlers: {
      success: (value: T[]) => R
      failure: (error: unknown) => R
    }
  ): Promise<R> => {
    return this.axiosClient.all(requests).then(handlers.success).catch(handlers.failure)
  }

  public request = <T, R = void>(request: AxiosRequestConfig, handlers: CallbackHandlers<T, R>): Promise<R> => {
    const mergedRequest = {
      ...this.config,
      ...request,
      headers: {
        ...(this.config as Record<string, any>)?.headers,
        ...request?.headers
      }
    }

    return this.axiosClient.request<T>(mergedRequest).then(handlers.success).catch(handlers.failure)
  }

  public retry = <T>(originalRequest: AxiosRequestConfig): Promise<AxiosResponse<T>> => {
    return this.axiosClient.retry(originalRequest)
  }

  public get interceptors() {
    return this.axiosClient.interceptors
  }
}

export default HttpClient
